import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Avatar, Badge, Button } from '../../components/ui';
import { TIMELINE_FILTERS, filterTimeline } from './timelineItems';
import type { TimelineFilter, TimelineItem, TimelineKind } from './timelineItems';
import './profileSection.css';

/**
 * The activity list, drawn from items already merged and sorted.
 *
 * Filter chips like HubSpot's tabs. A chip shows its count so an empty
 * filter reads as "0", not as a list that failed to load. The rows carry a
 * small mark per kind rather than an icon font: in, out, note, task,
 * change, order. Newest first is the caller's promise (mergeTimeline).
 */
const MARKS: Record<TimelineKind, { glyph: string; label: string }> = {
  email_in: { glyph: '↓', label: 'email received' },
  email_out: { glyph: '↑', label: 'email sent' },
  touch: { glyph: '☏', label: 'call or meeting' },
  note: { glyph: '≡', label: 'note' },
  task: { glyph: '✓', label: 'task' },
  change: { glyph: 'Δ', label: 'change' },
  order: { glyph: 'P', label: 'order' },
};

function when(iso: string): { day: string; time: string } {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { day: iso.slice(0, 10), time: '' };
  const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  // A date-only value (an order's delivered date) has no time worth showing.
  const time =
    iso.length <= 10
      ? ''
      : date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return { day, time };
}

export function Timeline({
  items,
  initialLimit = 25,
  actions,
  filter: fixedFilter,
  empty,
}: {
  items: readonly TimelineItem[];
  initialLimit?: number;
  /** Controls under a row, e.g. the lead timeline's admin "Remove entry". */
  actions?: (item: TimelineItem) => ReactNode;
  /**
   * Show only this slice, and draw no chips.
   *
   * For a caller that has already put the filters somewhere else — the deal
   * drawer moved them to its own tab row (deals/drawerTabs.ts), and a second
   * row of the same choices inside the list would be two controls for one
   * decision. Left undefined, the component owns the filter as before.
   */
  filter?: TimelineFilter;
  /** What an empty slice says. Defaults to the general line. */
  empty?: string;
}) {
  const [ownFilter, setFilter] = useState<TimelineFilter>('all');
  const [limit, setLimit] = useState(initialLimit);
  const filter = fixedFilter ?? ownFilter;
  const shown = filterTimeline(items, filter);
  const visible = shown.slice(0, limit);

  return (
    <div className="timeline">
      {fixedFilter === undefined ? (
        <div className="timeline-filters">
          {TIMELINE_FILTERS.map(({ key, label }) => {
            const n = filterTimeline(items, key).length;
            return (
              <Button
                key={key}
                size="sm"
                variant={filter === key ? 'primary' : 'secondary'}
                onClick={() => {
                  setFilter(key);
                  setLimit(initialLimit);
                }}
              >
                {label} <span className="timeline-filter-count">{n}</span>
              </Button>
            );
          })}
        </div>
      ) : null}

      {visible.length === 0 ? (
        <p className="profile-empty">{empty ?? 'Nothing here yet.'}</p>
      ) : (
        <ol className="timeline-list">
          {visible.map((item) => {
            const mark = MARKS[item.kind];
            const { day, time } =
              item.when === undefined ? when(item.at) : { day: item.when, time: '' };
            return (
              <li key={item.id} className="timeline-row">
                <div className="timeline-when">
                  <span>{day}</span>
                  {time === '' ? null : <small>{time}</small>}
                </div>
                <div
                  className="timeline-mark"
                  aria-label={
                    item.author === undefined ? mark.label : `Written by ${item.author.name}`
                  }
                >
                  {item.author === undefined ? (
                    <span className={`timeline-glyph timeline-glyph--${item.kind}`}>
                      {mark.glyph}
                    </span>
                  ) : (
                    <Avatar name={item.author.name} imageUrl={item.author.avatarUrl} size="sm" />
                  )}
                </div>
                <div className="timeline-what">
                  <div className="timeline-title">
                    {item.href === undefined ? (
                      item.title
                    ) : (
                      <Link to={item.href}>{item.title}</Link>
                    )}
                    {item.badges?.map((badge) => (
                      <Badge key={badge.label} tone={badge.tone}>
                        {badge.label}
                      </Badge>
                    ))}
                  </div>
                  {item.detail === undefined ? null : (
                    <div className="timeline-detail">{item.detail}</div>
                  )}
                  {item.who === undefined ? null : <div className="timeline-who">{item.who}</div>}
                  {actions === undefined ? null : actions(item)}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {shown.length > visible.length ? (
        <Button variant="ghost" size="sm" onClick={() => setLimit((n) => n + initialLimit)}>
          Show {Math.min(initialLimit, shown.length - visible.length)} older
        </Button>
      ) : null}
    </div>
  );
}
